// Source of truth for the PROCESS FORM CONTRACT — the vocabulary of value
// types and input refinements the UI actually uses to determine a field's
// type, and the constraints each resulting field supports.
//
// Every identifier below is the REAL literal string used today, with ONE
// deliberate exception: `number`. The actual wire value both
// `mapEditorTypeToValueType` in peak.editor.workflow's `utils/pssHelper.ts`
// and `simpleFieldType` in peak.client.web.ui2's `domain/form.js` check is
// literally `"long"` — `number` is only this editor's own UI label. It's
// used here anyway (accepted divergence, to be reconciled later) because it
// reads correctly alongside `input: "integer"` / `input: "float"` — a bare
// `type: "number"` is ambiguous precision, refined by `input` into one or
// the other. Any consumer serializing this onto the real wire must still
// write `"long"`, not `"number"`.
// - `valueTypes` matches `mapEditorTypeToValueType` in peak.editor.workflow's
//   `utils/pssHelper.ts` (what this editor writes as `ValueType`) and
//   `simpleFieldType` in peak.client.web.ui2's `domain/form.js` (what it
//   reads to resolve a field with no recognized `input`).
// - `fieldInputs` is the FULL vocabulary of resolved field identities — both
//   the literal `properties.input` values recognized by `form.js`'s
//   `addFieldType` switch (`textarea`, `float`, `integer`, `select`,
//   `dialog`, `terms`, `pdfViewer`, `badgePhoto`, `badgePreviewFront`,
//   `badgePreviewBack`) AND the "no input given, base `type` alone decides"
//   resolutions (`text`, `switch`, `date`, `datetime`) that
//   `defaultFieldForValueType` produces. `switch` renames form.js's bare-bool
//   resolution to match `peak.client.web.ui2`'s actual rendering component,
//   `packages/elements/src/molecules/FormField/Switch.tsx` — like `number`,
//   an accepted divergence from form.js's own naming, not a literal
//   `properties.input` value it checks for. `dateRangeBegin`/`dateRangeEnd`
//   are a THIRD divergence: on the real wire both sides share the single
//   literal `input: "dateRange"`, distinguished only by
//   `properties.dateRangeSide` ("begin"/"end") — split here into two inputs
//   because the begin/end sides genuinely support different constraints
//   (see `fieldConstraints`), which one shared `dateRange` entry couldn't
//   represent.
//
// Every vocabulary here is a real `as const` array with a derived TS type,
// not a bare `export type X = "a" | "b"` union — the latter disappears at
// compile time and can't be iterated/validated by any consumer at runtime.
//
// What's still deliberately NOT prescribed: how a consumer physically
// encodes `type`/`input` on the wire (a flat string-properties dictionary,
// a typed DTO, ...) — e.g. peak.client.web.ui2 reads `properties.input` from
// a `Record<string,string>` bag, while peak.editor.workflow's own
// `PSS.Forms` extension stores the resolved input as `Extensions.input`
// alongside a native-boolean, non-dictionary field set. Both place the same
// two real values (value type, input) somewhere; where exactly is each
// consumer's own serialization concern. See VALIDATION.md.

/**
 * The underlying value type of a field — set independently of any input
 * refinement below. Backed by a real array (not just a TS union) so any
 * consumer — JS or TS — can iterate/validate against it at runtime, not only
 * at compile time.
 *
 * `present` is not a user-data value type like the rest — it marks fields
 * that display something (a PDF, a captured photo) and track
 * completion/production state, rather than collecting a value of a given
 * type from the user. It exists so `pdfViewer`/`badgePhoto`/
 * `badgePreviewFront`/`badgePreviewBack` aren't misdescribed as accepting
 * "any of the 6 data value types" below, when in reality none of those
 * apply to them at all.
 */
export const valueTypes = [
  "string",
  "number",
  "bool",
  "date",
  "datetime",
  "enum",
  "present",
] as const;
export type ValueType = (typeof valueTypes)[number];

/** The subset of `valueTypes` that represent real, user-data-bearing values. */
export const dataValueTypes = [
  "string",
  "number",
  "bool",
  "date",
  "datetime",
  "enum",
] as const;
export type DataValueType = (typeof dataValueTypes)[number];

/**
 * Every resolved field identity — the full set of things a field can
 * actually render as. `text`, `switch`, `date`, `datetime` are reached with
 * no `properties.input` at all (base `type` alone decides, via
 * `defaultFieldForValueType`); the rest are reached by setting `input` to
 * that literal value.
 */
export const fieldInputs = [
  "text",
  "textarea",
  "integer",
  "float",
  "switch",
  "terms",
  "date",
  "datetime",
  "dateRangeBegin",
  "dateRangeEnd",
  "dialog",
  "select",
  "pdfViewer",
  "badgePhoto",
  "badgePreviewFront",
  "badgePreviewBack",
] as const;
export type FieldInput = (typeof fieldInputs)[number];

/**
 * Which value type(s) each input is legal on — listed explicitly, in full,
 * per input (the value type still determines what the underlying value
 * carried alongside it is, e.g. for `select`/`dialog`). Pairing an input
 * with a value type outside its list is invalid — e.g. `input: "textarea"`
 * on `type: "number"` — and matches real thrown behavior
 * (`ConfigurationErrorBadProcess`, error `BadFieldType`) in `form.js`
 * (real wire type `"long"` — see the `number` note at the top of this file).
 *
 * `pdfViewer`/`badgePhoto`/`badgePreviewFront`/`badgePreviewBack` only ever
 * pair with `"present"` — never with a data value type — since they
 * don't collect a value at all (see `valueTypes` comment above).
 */
export const inputAllowedValueTypes: Record<FieldInput, ValueType[]> = {
  text: ["string"],
  textarea: ["string"],
  integer: ["number"],
  float: ["number"],
  switch: ["bool"],
  date: ["date"],
  datetime: ["datetime"],
  terms: ["bool"],
  select: [...dataValueTypes],
  dialog: [...dataValueTypes],
  dateRangeBegin: ["date"],
  dateRangeEnd: ["date"],
  pdfViewer: ["present"],
  badgePhoto: ["present"],
  badgePreviewFront: ["present"],
  badgePreviewBack: ["present"],
};

/**
 * Resolution when no (recognized) `input` is set — the value type alone
 * determines the field.
 */
export const defaultFieldForValueType: Record<ValueType, FieldInput> = {
  bool: "switch",
  date: "date",
  datetime: "datetime",
  string: "text",
  number: "integer",
  enum: "select",
  present: "pdfViewer",
};

/**
 * Semantic value type of a property or constraint entry. Says nothing about
 * wire encoding. Goes beyond the generic JS primitives where the real code
 * actually cares about a more specific shape:
 * - `"integer"`/`"float"` — deliberately not a generic `"number"`: a
 *   `minValue`/`maxValue` on an `integer` field is a whole number, on a
 *   `float` field it isn't, and collapsing both to "number" would lose that.
 * - `"regex"` — a regex source string (`pattern`).
 * - `"datetime"` — an absolute ISO datetime string. There's no separate
 *   "date-only" wire format: `parseDateValue` (used even for plain `date`
 *   fields, not just `datetime`) parses via `parseISOString(value,
 *   formatModes.FIXED_DAY)` — a full ISO datetime is what's actually
 *   accepted, the time-of-day portion is simply discarded/truncated to the
 *   day afterward. Modeling a distinct "date" wire type would claim a
 *   narrower accepted format than what the code really parses.
 * - `"duration"` — specifically an ISO8601 duration string (e.g. `"P10D"`),
 *   as parsed by `duration-fns`.
 * Every atomic type here — never a compound name like "date-or-duration".
 * A property that accepts more than one shape lists multiple entries in
 * `ExtensionDescriptor.valueType` rather than inventing a combined type name
 * for it. `earliest`/`latest`/`beginLatest`/`endEarliest`/`endLatest` are a
 * three-way case, not two: `form.js`'s `parseOffsetDays`/`parseOffsetDuration`
 * try a plain signed `"integer"` day count FIRST, and only fall back to
 * parsing the same string as an ISO8601 `"duration"` if that fails — so
 * these properties are `["datetime", "integer", "duration"]`, reusing the
 * same `"integer"` atomic type as `minValue`/`maxValue` rather than folding
 * the day-count form into `"duration"`.
 * - `"base64"` — base64-encoded binary content (`pdfViewer.content`).
 * Plain identifiers/labels/paths (`group`, `helperKey`, `object`,
 * `dateRangeKey`, ...) stay `"string"` — they're just text, nothing more
 * specific to say about their shape.
 */
export const extensionValueTypes = [
  "string",
  "integer",
  "float",
  "boolean",
  "string[]",
  "regex",
  "datetime",
  "duration",
  "base64",
  "selectOptions",
] as const;
export type ExtensionValueType = (typeof extensionValueTypes)[number];

/**
 * A single unified descriptor shape, used for both real validation
 * constraints (`fieldConstraints`, e.g. `minValue`) and non-constraint field
 * metadata/properties (`commonProperties`, `fieldProperties`, e.g. `hidden`,
 * `dateRangeKey`). One shape for both, since both are just "a named,
 * typed, described thing a field can carry" — only the container they're
 * grouped into (`fieldConstraints` vs. `commonProperties`/`fieldProperties`)
 * says whether a given entry is a value constraint or plain metadata.
 *
 * `valueType` is always an array, even for single-type entries — this keeps
 * every entry the same shape, and lets a property that genuinely accepts
 * more than one type (e.g. `earliest`: `["datetime", "integer", "duration"]`)
 * say so directly instead of needing a separate compound type.
 */
export interface ExtensionDescriptor {
  valueType: ExtensionValueType[];
  description: string;
  required?: boolean;
}

/**
 * Properties valid on every field, regardless of type/input. None of these
 * are validation constraints on the field's own value — they're
 * metadata/behavior (visibility, grouping, localization, defaults, dynamic
 * modification). `required` is a genuine constraint and lives in
 * `fieldConstraints` instead, explicit on every resolved field rather than
 * implicitly inherited from a shared bag.
 */
export const commonProperties: Record<string, ExtensionDescriptor> = {
  hidden: { valueType: ["boolean"], description: "Field is not rendered." },
  disabled: {
    valueType: ["boolean"],
    description: "Field is rendered but not editable.",
  },
  group: {
    valueType: ["string"],
    description: "Groups fields for layout purposes.",
  },
  variant: { valueType: ["string"], description: "Rendering variant hint." },
  context: {
    valueType: ["string"],
    description:
      "Which operation context the field applies to (e.g. default vs. reject).",
  },
  helperKey: {
    valueType: ["string"],
    description: "Localization key for helper/hint text shown below the field.",
  },
  formStep: {
    valueType: ["string"],
    description: "Which step of a multi-step form the field belongs to.",
  },
  preview: {
    valueType: ["boolean"],
    description:
      "Field is cause for a preview invalidation in the Production module.",
  },
  modifier: {
    valueType: ["string"],
    description:
      "Key of a modifier group that can dynamically alter this field (hidden/disabled/required/value) based on other field values.",
  },
  object: {
    valueType: ["string"],
    description:
      "Allows for field nesting by giving an address path (eg. 'Visitor::FirstName'). Supports multiple content domains (eg. 'Visitor[Select]::Id')",
  },
};

const dateValueOffset: ExtensionDescriptor = {
  valueType: ["integer", "duration"],
  description: "Offsets the value by a relative amount.",
};

/**
 * Properties specific to one resolved field type that aren't validation
 * constraints on the field's own value.
 * `dateRangeBegin`/`dateRangeEnd` both carry `dateRangeKey`, which links the
 * pair — must match exactly between the two. There's no separate
 * `dateRangeSide` property: which side a field is on is already the
 * `FieldInput` itself (`dateRangeBegin` vs. `dateRangeEnd`), so recording it
 * again as a string property would just be duplicate information. Missing
 * `dateRangeKey`, or missing a matching partner field with the same key, is
 * a real runtime failure in `peak.client.web.ui2`
 * (`ConfigurationErrorBadProcess`, error `NoRangeEnd`).
 */
export const fieldProperties: Partial<
  Record<FieldInput, Record<string, ExtensionDescriptor>>
> = {
  date: {
    defaultValueOffset: dateValueOffset,
  },
  datetime: {
    defaultValueOffset: dateValueOffset,
  },
  dateRangeBegin: {
    dateRangeKey: {
      valueType: ["string"],
      description:
        "Links this field to its end-bound pair — must match the partner field's dateRangeKey exactly.",
      required: true,
    },
    defaultValueOffset: dateValueOffset,
    earliestOffset: dateValueOffset,
    latestOffset: dateValueOffset,
    beginEarliestOffset: dateValueOffset,
    beginLatestOffset: dateValueOffset,
  },
  dateRangeEnd: {
    dateRangeKey: {
      valueType: ["string"],
      description:
        "Links this field to its start-bound pair — must match the partner field's dateRangeKey exactly.",
      required: true,
    },
    defaultValueOffset: dateValueOffset,
    endEarliestOffset: dateValueOffset,
    endLatestOffset: dateValueOffset,
  },
  text: {
    formatExample: {
      valueType: ["string"],
      description: "Example value shown in format-mismatch errors.",
    },
  },
  textarea: {
    formatExample: {
      valueType: ["string"],
      description: "Example value shown in format-mismatch errors.",
    },
  },
  select: {
    options: {
      valueType: ["string", "selectOptions"],
      description:
        'Either a datasource or an options object in the shape `{"LabelKey": value}`',
      required: true,
    },
  },
  pdfViewer: {
    pdfBase64: {
      valueType: ["base64"],
      description: "The PDF content to display.",
      required: true,
    },
    filename: { valueType: ["string"], description: "Display filename." },
    viewerHeight: {
      valueType: ["integer"],
      description: "Height of the embedded viewer, in pixels.",
    },
    noPrint: { valueType: ["boolean"], description: "Hides the print action." },
    noDownload: {
      valueType: ["boolean"],
      description: "Hides the download action.",
    },
    noViewer: {
      valueType: ["boolean"],
      description:
        "Hides the embedded PDF preview, leaving only the surrounding controls.",
    },
    notCollapsable: {
      valueType: ["boolean"],
      description: "Prevents the viewer from being collapsed.",
    },
    initiallyCollapsed: {
      valueType: ["boolean"],
      description: "Viewer starts collapsed.",
    },
    printDpi: {
      valueType: ["integer"],
      description:
        "DPI used when rendering the PDF for printing (defaults to 300dpi).",
    },
  },
};

const required: ExtensionDescriptor = {
  valueType: ["boolean"],
  description: "Field must have a value before the form can be submitted.",
};

export const fieldConstraints: Record<
  FieldInput,
  Record<string, ExtensionDescriptor>
> = {
  text: {
    required,
    pattern: {
      valueType: ["regex"],
      description: "Regex the value must match.",
    },
    format: {
      valueType: ["string"],
      description: "Named format identifier (e.g. an email/phone format).",
    },
  },
  textarea: {
    required,
    pattern: {
      valueType: ["regex"],
      description: "Regex the value must match.",
    },
    format: { valueType: ["string"], description: "Named format identifier." },
  },
  integer: {
    required,
    minValue: { valueType: ["integer"], description: "Minimum allowed value." },
    maxValue: { valueType: ["integer"], description: "Maximum allowed value." },
  },
  float: {
    required,
    minValue: { valueType: ["float"], description: "Minimum allowed value." },
    maxValue: { valueType: ["float"], description: "Maximum allowed value." },
  },
  date: {
    required,
    earliest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Earliest allowed date (absolute or relative offset).",
    },
    latest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Latest allowed date (absolute or relative offset).",
    },
  },
  datetime: {
    required,
    earliest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Earliest allowed datetime (absolute or relative offset).",
    },
    latest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Latest allowed datetime (absolute or relative offset).",
    },
  },
  dateRangeBegin: {
    required,
    earliest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Earliest allowed start date (aka beginEarliest).",
    },
    latest: {
      valueType: ["datetime", "integer", "duration"],
      description:
        "Fallback latest allowed end date — used only when the paired dateRangeEnd field doesn't set its own endLatest.",
    },
    beginEarliest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Earliest allowed start date.",
    },
    beginLatest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Latest allowed start date.",
    },
    minLength: {
      valueType: ["integer"],
      description: "Minimum span, in days, between start and end.",
    },
    maxLength: {
      valueType: ["integer"],
      description: "Maximum span, in days, between start and end.",
    },
  },
  dateRangeEnd: {
    required,
    endEarliest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Earliest allowed end date.",
    },
    endLatest: {
      valueType: ["datetime", "integer", "duration"],
      description: "Latest allowed end date.",
    },
  },
  select: {
    required,
  },
  dialog: {
    required,
  },
  terms: { required },
  switch: {},
  pdfViewer: {
    printRequired: {
      valueType: ["boolean"],
      description: "User must print before the field is considered complete.",
    },
    downloadRequired: {
      valueType: ["boolean"],
      description:
        "User must download before the field is considered complete.",
    },
  },
  badgePhoto: {},
  badgePreviewFront: {},
  badgePreviewBack: {},
};
